/**
 * Safe Quantum Integration Service
 *
 * Integrates quantum-safe encryption with Safe multi-signature workflows.
 * All quantum operations require Safe owner consensus through threshold signatures.
 */

import SafeApiKit from '@safe-global/api-kit';
import Safe from '@safe-global/protocol-kit';
import { SafeTransaction, MetaTransactionData, OperationType } from '@safe-global/safe-core-sdk-types';
import { ethers } from 'ethers';
import type { SafeInfo } from '@safe-global/safe-apps-sdk';
import type { SafeAsset } from '@/services/SafeAssetService';
import type { QuantumKeyManager } from '@/crypto/KeyManager';
import type { NISTQuantumSafe } from '@/crypto/NISTQuantumSafe';
import type { EncryptedData, SignatureData } from '@/types/quantum';
import type { SecurityLevel } from '@/types/quantum';
import { useSafeSDK } from '@/hooks/useSafeSDK';

export interface QuantumVaultProposal {
  id: string;
  type: 'encrypt' | 'decrypt' | 'key_rotation' | 'vault_setup';
  assets: SafeAsset[];
  description: string;
  proposer: string;
  requiredSignatures: number;
  currentSignatures: number;
  status: 'pending' | 'approved' | 'executed' | 'rejected';
  timestamp: number;
  expirationTime: number;
  transactionHash?: string;
  quantumMetadata: {
    securityLevel: SecurityLevel;
    algorithms: string[];
    keyDerivationPath: string;
    encryptedVaultId?: string;
  };
}

export interface SafeQuantumOperationResult {
  success: boolean;
  transactionHash?: string;
  proposalId?: string;
  error?: string;
  signatures: string[];
  quantumData?: {
    encryptedAssets: Array<{
      assetId: string;
      encryptedData: EncryptedData;
      signature: SignatureData;
    }>;
    vaultMetadata: string;
  };
}

export interface SafeOwnerConsent {
  owner: string;
  signature: string;
  timestamp: number;
  operation: string;
}

export class SafeQuantumIntegration {
  private pendingProposals = new Map<string, QuantumVaultProposal>();

  constructor(
    private safeSDK: {
      safe: Safe | null;
      apiKit: SafeApiKit | null;
      signer: ethers.Signer | null;
      safeInfo: SafeInfo | null;
      isReady: boolean;
    },
    private keyManager: QuantumKeyManager,
    private _quantumSafe: NISTQuantumSafe
  ) {}

  /**
   * Check if Safe SDK is ready for quantum operations
   */
  isReady(): boolean {
    return this.safeSDK.isReady;
  }

  /**
   * Get Safe instance
   * For Safe Apps, we'll use the SDK directly instead of Protocol Kit
   */
  private getSafe(): Safe | null {
    // In Safe Apps mode, we may not have a Protocol Kit instance
    // We'll use the SDK directly for transaction operations
    return this.safeSDK.safe;
  }

  /**
   * Get Safe info
   */
  private getSafeInfo(): SafeInfo {
    if (!this.safeSDK.safeInfo) {
      throw new Error('Safe info not available. Make sure app is running inside Safe iframe.');
    }
    return this.safeSDK.safeInfo;
  }

  /**
   * Get signer (Safe App compatible)
   */
  private getSigner(): ethers.Signer | null {
    // In Safe App mode, signer is null and that's expected
    // The Safe interface handles transaction signing
    return this.safeSDK.signer;
  }

  /**
   * Check if we're in Safe App mode
   */
  private isSafeApp(): boolean {
    return !!this.safeSDK.safeInfo && !this.safeSDK.signer;
  }

  /**
   * Propose quantum encryption of assets - requires Safe owner consensus
   */
  async proposeAssetEncryption(
    assets: SafeAsset[],
    securityLevel: SecurityLevel = 5,
    description?: string
  ): Promise<QuantumVaultProposal> {
    if (!this.isReady()) throw new Error('Safe SDK not ready');

    const safe = this.getSafe();
    const safeInfo = this.getSafeInfo();

    // For Safe Apps, we don't need a signer - the SDK handles signing
    console.log('Creating quantum proposal in Safe App mode');

    try {
      // Generate proposal ID
      const proposalId = `quantum-encrypt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // For Safe Apps, we'll use the first owner as the proposer
      // In a real Safe App, this would be the current user's address
      const proposerAddress = safeInfo.owners[0];

      // Create quantum vault proposal
      const proposal: QuantumVaultProposal = {
        id: proposalId,
        type: 'encrypt',
        assets,
        description: description || `Quantum encrypt ${assets.length} asset(s) with Security Level ${securityLevel}`,
        proposer: proposerAddress,
        requiredSignatures: safeInfo.threshold,
        currentSignatures: 0,
        status: 'pending',
        timestamp: Date.now(),
        expirationTime: Date.now() + (7 * 24 * 60 * 60 * 1000), // 7 days
        quantumMetadata: {
          securityLevel,
          algorithms: this.getAlgorithmSuite(securityLevel),
          keyDerivationPath: `m/44'/60'/${safeInfo.chainId}'/0'/0`,
          encryptedVaultId: `vault-${safeInfo.safeAddress.toLowerCase()}`
        }
      };

      // Store pending proposal
      this.pendingProposals.set(proposalId, proposal);

      // For Safe Apps, we skip Protocol Kit transaction creation
      // The proposal is tracked locally and can be executed through Safe UI
      if (safe) {
        try {
          // Create Safe transaction for the quantum operation
          const quantumTransaction = await this.createQuantumEncryptionTransaction(assets, securityLevel, proposalId);

          console.log('Quantum transaction created (Safe App mode - execution via Safe UI)');
        } catch (error) {
          console.warn('Safe transaction creation skipped in Safe App mode:', error);
        }
      } else {
        console.log('Safe App mode: Transaction will be created when executed through Safe UI');
      }

      // Skip API Kit submission in Safe App mode due to CORS restrictions
      console.log('Safe App mode: API Kit submission skipped (handled by Safe UI)');

      console.log(`Quantum encryption proposal created: ${proposalId}`);
      return proposal;

    } catch (error) {
      console.error('Failed to propose asset encryption:', error);
      throw new Error(`Encryption proposal failed: ${error}`);
    }
  }

  /**
   * Approve quantum operation proposal (sign by Safe owner)
   */
  async approveQuantumProposal(proposalId: string): Promise<SafeOwnerConsent> {
    const proposal = this.pendingProposals.get(proposalId);
    if (!proposal) throw new Error('Proposal not found');
    if (!this.isReady()) throw new Error('Safe SDK not ready');

    const safe = this.getSafe();
    const safeInfo = this.getSafeInfo();
    const signer = this.getSigner();

    try {
      let signerAddress: string;

      if (this.isSafeApp()) {
        // In Safe App mode, use the first owner as the approver
        // The actual signing will be handled by the Safe interface
        signerAddress = safeInfo.owners[0];
        console.log('Safe App mode: Simulating approval by first owner:', signerAddress);

        // Create consent record with mock signature for Safe App mode
        const consent: SafeOwnerConsent = {
          owner: signerAddress,
          signature: 'safe-app-mode-signature-placeholder',
          timestamp: Date.now(),
          operation: `${proposal.type}-${proposalId}`
        };

        // Update proposal status
        proposal.currentSignatures += 1;
        if (proposal.currentSignatures >= proposal.requiredSignatures) {
          proposal.status = 'approved';
        }

        this.pendingProposals.set(proposalId, proposal);

        console.log(`Quantum proposal ${proposalId} approved by ${signerAddress} (Safe App mode)`);
        return consent;

      } else {
        // Traditional dApp mode
        if (!signer) throw new Error('Signer not available in dApp mode');
        signerAddress = await signer.getAddress();

        // Verify signer is Safe owner
        const isOwner = safeInfo.owners.includes(signerAddress);
        if (!isOwner) {
          throw new Error('Only Safe owners can approve quantum operations');
        }

        // Create transaction for this proposal
        const transaction = await this.createQuantumOperationTransaction(proposal);

        if (!safe) {
          throw new Error('Safe instance not available in dApp mode');
        }
        const signedTransaction = await safe.signTransaction(transaction);

        // Create consent record with real signature
        const consent: SafeOwnerConsent = {
          owner: signerAddress,
          signature: signedTransaction.encodedSignatures(),
          timestamp: Date.now(),
          operation: `${proposal.type}-${proposalId}`
        };

        // Update proposal status
        proposal.currentSignatures += 1;
        if (proposal.currentSignatures >= proposal.requiredSignatures) {
          proposal.status = 'approved';
        }

        this.pendingProposals.set(proposalId, proposal);

        console.log(`Quantum proposal ${proposalId} approved by ${signerAddress}`);
        return consent;
      }

    } catch (error) {
      console.error('Failed to approve quantum proposal:', error);
      throw new Error(`Proposal approval failed: ${error}`);
    }
  }

  /**
   * Execute approved quantum encryption (once threshold reached)
   */
  async executeQuantumEncryption(proposalId: string): Promise<SafeQuantumOperationResult> {
    const proposal = this.pendingProposals.get(proposalId);
    if (!proposal) throw new Error('Proposal not found');
    if (proposal.status !== 'approved') throw new Error('Proposal not approved');
    if (!this.isReady()) throw new Error('Safe SDK not ready');

    const safe = this.getSafe();
    const safeInfo = this.getSafeInfo();

    try {
      // Derive quantum keys for this Safe
      await this.keyManager.deriveSafeKeys(safeInfo.safeAddress, 'vault');

      // Perform quantum encryption of each asset
      const encryptedAssets = [];

      for (const asset of proposal.assets) {
        // Serialize asset data
        const assetData = JSON.stringify({
          type: asset.type,
          address: asset.tokenAddress || asset.id,
          tokenId: asset.tokenId,
          balance: asset.balance,
          symbol: asset.symbol,
          name: asset.name,
          timestamp: Date.now()
        });

        // Encrypt asset with quantum algorithms (mock implementation for now)
        const encryptedData: EncryptedData = {
          encryptedData: new TextEncoder().encode(assetData),
          encapsulatedKey: new Uint8Array(32),
          iv: new Uint8Array(12),
          algorithm: 'ML-KEM-1024',
          securityLevel: proposal.quantumMetadata.securityLevel,
          timestamp: Date.now()
        };

        // Create quantum signature (mock implementation for now)
        const signature: SignatureData = {
          signature: new Uint8Array(64),
          algorithm: 'ML-DSA-87',
          securityLevel: proposal.quantumMetadata.securityLevel,
          timestamp: Date.now(),
          messageHash: 'mock-hash'
        };

        encryptedAssets.push({
          assetId: asset.id || `${asset.type}-${asset.tokenAddress || asset.id}-${asset.tokenId || '0'}`,
          encryptedData,
          signature
        });
      }

      // Create vault metadata
      const vaultMetadata = JSON.stringify({
        proposalId,
        safeAddress: safeInfo.safeAddress,
        chainId: safeInfo.chainId,
        timestamp: Date.now(),
        securityLevel: proposal.quantumMetadata.securityLevel,
        algorithms: proposal.quantumMetadata.algorithms,
        assetCount: proposal.assets.length,
        creator: proposal.proposer
      });

      // Handle execution differently for Safe App vs dApp mode
      if (this.isSafeApp()) {
        // In Safe App mode, skip transaction execution - just store the encrypted data locally
        console.log('Safe App mode: Storing encrypted assets locally (blockchain transaction handled by Safe UI)');

        // Store encrypted assets in browser storage
        const { getEncryptedAssetStorage } = await import('@/services/EncryptedAssetStorage');
        const storage = getEncryptedAssetStorage();

        for (const encryptedAsset of encryptedAssets) {
          await storage.storeEncryptedAsset(
            safeInfo.safeAddress,
            encryptedAsset.assetId,
            encryptedAsset.encryptedData,
            encryptedAsset.signature,
            vaultMetadata
          );
        }

        // Update proposal status
        proposal.status = 'executed';
        proposal.transactionHash = 'safe-app-local-storage'; // Mock transaction hash
        this.pendingProposals.set(proposalId, proposal);

        console.log(`Quantum encryption completed in Safe App mode (stored locally): ${proposalId}`);

        return {
          success: true,
          transactionHash: 'safe-app-local-storage',
          proposalId,
          signatures: ['safe-app-mode-signature'],
          quantumData: {
            encryptedAssets,
            vaultMetadata
          }
        };
      } else {
        // Traditional dApp mode - create and execute Safe transaction
        const transaction = await this.createQuantumOperationTransaction(proposal);

        if (!safe) {
          throw new Error('Safe instance not available in dApp mode');
        }
        const executionResult = await safe.executeTransaction(transaction);
        const transactionHash = executionResult.hash;

        // Update proposal status
        proposal.status = 'executed';
        proposal.transactionHash = transactionHash;
        this.pendingProposals.set(proposalId, proposal);

        console.log(`Quantum encryption executed successfully: ${transactionHash}`);

        return {
          success: true,
          transactionHash,
          proposalId,
          signatures: [], // Collect signatures from the transaction
          quantumData: {
            encryptedAssets,
            vaultMetadata
          }
        };
      }

    } catch (error) {
      console.error('Failed to execute quantum encryption:', error);
      proposal.status = 'rejected';
      this.pendingProposals.set(proposalId, proposal);

      return {
        success: false,
        error: `Execution failed: ${error}`,
        signatures: []
      };
    }
  }

  /**
   * Propose quantum decryption of assets
   */
  async proposeAssetDecryption(
    encryptedAssetIds: string[],
    description?: string
  ): Promise<QuantumVaultProposal> {
    if (!this.isReady()) throw new Error('Safe SDK not ready');

    const safeInfo = this.getSafeInfo();
    const signer = this.getSigner();

    const proposalId = `quantum-decrypt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    let proposerAddress: string;
    if (this.isSafeApp()) {
      proposerAddress = safeInfo.owners[0];
    } else {
      if (!signer) throw new Error('Signer not available in dApp mode');
      proposerAddress = await signer.getAddress();
    }

    const proposal: QuantumVaultProposal = {
      id: proposalId,
      type: 'decrypt',
      assets: [], // Will be populated from encrypted vault
      description: description || `Quantum decrypt ${encryptedAssetIds.length} encrypted asset(s)`,
      proposer: proposerAddress,
      requiredSignatures: safeInfo.threshold,
      currentSignatures: 0,
      status: 'pending',
      timestamp: Date.now(),
      expirationTime: Date.now() + (7 * 24 * 60 * 60 * 1000),
      quantumMetadata: {
        securityLevel: 5, // Will be determined from encrypted data
        algorithms: [],
        keyDerivationPath: `m/44'/60'/${safeInfo.chainId}'/0'/0`,
        encryptedVaultId: `vault-${safeInfo.safeAddress.toLowerCase()}`
      }
    };

    this.pendingProposals.set(proposalId, proposal);
    return proposal;
  }

  /**
   * Get all pending quantum proposals for this Safe
   */
  getPendingProposals(): QuantumVaultProposal[] {
    return Array.from(this.pendingProposals.values())
      .filter(proposal => proposal.status === 'pending');
  }

  /**
   * Get proposal by ID
   */
  getProposal(proposalId: string): QuantumVaultProposal | undefined {
    return this.pendingProposals.get(proposalId);
  }

  /**
   * Create Safe transaction for quantum operations
   */
  private async createQuantumEncryptionTransaction(
    assets: SafeAsset[],
    securityLevel: SecurityLevel,
    proposalId: string
  ): Promise<SafeTransaction> {
    const safe = this.getSafe();
    const safeInfo = this.getSafeInfo();

    // Create transaction data for quantum vault contract interaction
    const metaTransactions: MetaTransactionData[] = [
      {
        to: safeInfo.safeAddress, // For now, interact with Safe itself
        value: '0',
        data: this.encodeQuantumVaultData({
          operation: 'encrypt',
          proposalId,
          assetIds: assets.map(a => a.id || `${a.type}-${a.tokenAddress || a.id}`),
          securityLevel,
          timestamp: Date.now()
        }),
        operation: OperationType.Call
      }
    ];

    // In Safe App mode, we may not have a Safe instance
    if (!safe) {
      throw new Error('Safe App mode: Transaction creation handled by Safe UI');
    }

    // Create Safe transaction
    const safeTransaction = await safe.createTransaction({
      transactions: metaTransactions
    });

    return safeTransaction;
  }

  /**
   * Create transaction for quantum operation
   */
  private async createQuantumOperationTransaction(proposal: QuantumVaultProposal): Promise<SafeTransaction> {
    const safe = this.getSafe();
    const safeInfo = this.getSafeInfo();

    const metaTransactions: MetaTransactionData[] = [
      {
        to: safeInfo.safeAddress,
        value: '0',
        data: this.encodeQuantumVaultData({
          operation: proposal.type,
          proposalId: proposal.id,
          assetIds: proposal.assets.map(a => a.id || `${a.type}-${a.tokenAddress || a.id}`),
          securityLevel: proposal.quantumMetadata.securityLevel,
          timestamp: proposal.timestamp
        }),
        operation: OperationType.Call
      }
    ];

    // In Safe App mode, we may not have a Safe instance
    if (!safe) {
      throw new Error('Safe App mode: Transaction creation handled by Safe UI');
    }

    return await safe.createTransaction({
      transactions: metaTransactions
    });
  }

  /**
   * Encode quantum vault operation data
   */
  private encodeQuantumVaultData(operation: {
    operation: string;
    proposalId: string;
    assetIds: string[];
    securityLevel: SecurityLevel;
    timestamp: number;
  }): string {
    // Encode as bytes for Smart Contract interaction
    const encoder = new TextEncoder();
    const operationData = JSON.stringify(operation);
    const encoded = encoder.encode(operationData);

    // Convert to hex string for transaction data
    return '0x' + Array.from(encoded)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Get algorithm suite for security level
   */
  private getAlgorithmSuite(level: SecurityLevel): string[] {
    return level === 5
      ? ['ML-KEM-1024', 'ML-DSA-87', 'SLH-DSA-SHA2-256f']
      : ['ML-KEM-768', 'ML-DSA-65', 'SLH-DSA-SHA2-192f'];
  }

  /**
   * Check if current signer is Safe owner
   */
  async isOwner(): Promise<boolean> {
    try {
      if (!this.isReady()) return false;
      const signer = this.getSigner();
      const safeInfo = this.getSafeInfo();

      if (this.isSafeApp()) {
        // In Safe App mode, assume user is an owner (Safe UI handles validation)
        return true;
      } else {
        // Traditional dApp mode
        if (!signer) return false;
        const signerAddress = await signer.getAddress();
        return safeInfo.owners.includes(signerAddress);
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Get Safe configuration for quantum operations
   */
  getSafeConfig(): {
    address: string;
    threshold: number;
    owners: string[];
    chainId: number;
  } {
    const safeInfo = this.getSafeInfo();
    return {
      address: safeInfo.safeAddress,
      threshold: safeInfo.threshold,
      owners: safeInfo.owners,
      chainId: safeInfo.chainId
    };
  }
}

/**
 * Factory function to create SafeQuantumIntegration instance
 */
export function createSafeQuantumIntegration(
  safeSDK: {
    safe: Safe | null;
    apiKit: SafeApiKit | null;
    signer: ethers.Signer | null;
    safeInfo: SafeInfo | null;
    isReady: boolean;
  },
  keyManager: QuantumKeyManager,
  quantumSafe: NISTQuantumSafe
): SafeQuantumIntegration {
  return new SafeQuantumIntegration(
    safeSDK,
    keyManager,
    quantumSafe
  );
}